export type ReleaseItemType = 'feature' | 'improvement' | 'fix' | 'security';

export interface ReleaseItem {
  type: ReleaseItemType;
  title: string;
  description?: string;
}

export interface Release {
  version: string;
  date: string; // YYYY-MM-DD
  /** Tóm tắt 1 dòng hiện trong popup "Có gì mới". */
  highlight: string;
  items: ReleaseItem[];
}

export const RELEASE_TYPE_META: Record<ReleaseItemType, { label: string; color: string }> = {
  feature: { label: 'Tính năng mới', color: 'green' },
  improvement: { label: 'Cải tiến', color: 'blue' },
  fix: { label: 'Sửa lỗi', color: 'orange' },
  security: { label: 'Bảo mật', color: 'red' },
};

/**
 * Nhật ký phát hành — bản mới nhất đặt TRÊN CÙNG.
 * Khi release tính năng mới: thêm một entry mới vào đầu mảng, tăng version.
 * Popup "Có gì mới" tự hiện cho user chưa xem bản đó (so với localStorage).
 */
export const RELEASES: Release[] = [
  {
    version: '1.10.0',
    date: '2026-11-01',
    highlight:
      'Chấm công theo tháng có duyệt trước khi tính lương, đơn nghỉ phép gửi email cho quản lý, và lịch nghỉ để cả nhóm cùng theo dõi.',
    items: [
      {
        type: 'feature',
        title: 'Bảng chấm công theo tháng',
        description:
          'Menu Nhân viên → Chấm công: đầu tháng hệ thống tự tạo bảng, mặc định đi làm T2–T7. Quản lý chỉ sửa ngày nghỉ, đặt ngày lễ cho cả cửa hàng, rồi duyệt. Kỳ lương chỉ tạo được sau khi bảng công đã duyệt.',
      },
      {
        type: 'feature',
        title: 'Đơn nghỉ phép',
        description:
          'Nhân viên tạo đơn với loại nghỉ, thời gian, lý do và người thay thế. Quản lý nhận email và thông báo trong app, duyệt xong ngày nghỉ tự ghi vào bảng chấm công.',
      },
      {
        type: 'feature',
        title: 'Lịch nghỉ',
        description:
          'Mọi nhân viên xem được ai nghỉ ngày nào và ai thay thế. Lý do nghỉ không hiển thị với đồng nghiệp.',
      },
      {
        type: 'feature',
        title: 'Bảng lương theo mẫu, hồ sơ nhân viên đầy đủ và Bảng BHXH',
        description:
          'Bảng lương và phiếu lương theo đúng mẫu đang dùng (có và không có BHXH), thêm phụ cấp đồng phục, chuyên cần, công tác phí, hoàn tiền phép. Danh sách nhân viên có thêm các cột hồ sơ theo mẫu TTNV; tab Bảng BHXH tự ghi báo tăng / giảm và xuất Excel.',
      },
      {
        type: 'improvement',
        title: 'Công chuẩn theo tháng',
        description:
          'Công chuẩn bằng số ngày T2–T7 thực tế của tháng, nên đi làm đủ tháng là đủ lương cơ bản. Nghỉ nửa ngày (P/2, KL/2) vẫn được tính nửa công cho buổi đi làm.',
      },
    ],
  },
  {
    version: '1.9.0',
    date: '2026-10-27',
    highlight:
      'Menu "Tin nhắn" mới: đọc và trả lời tin khách từ Messenger, Instagram, Zalo OA và TikTok ngay trong app, chia hội thoại cho nhân viên, tạo đơn nhanh từ hội thoại.',
    items: [
      {
        type: 'feature',
        title: 'Tin nhắn đa kênh',
        description:
          'Menu "Tin nhắn" gom tin khách gửi vào Fanpage (Messenger), Instagram, Zalo OA và TikTok vào một màn hình, tin mới hiện ngay không cần tải lại trang. Lọc theo kênh, trạng thái (Đang mở / Chờ xử lý / Đã đóng), Của tôi / Chưa đọc; tìm theo tên, SĐT hoặc nội dung. Trả lời bằng chữ hoặc tối đa 5 ảnh (mỗi ảnh ≤ 8MB). Mỗi hội thoại ghi rõ còn bao lâu để trả lời: Messenger/Instagram 24 giờ, TikTok 48 giờ, Zalo OA khoảng 7 ngày kể từ tin cuối của khách.',
      },
      {
        type: 'feature',
        title: 'Chia hội thoại cho nhân viên',
        description:
          'Trả lời một hội thoại chưa ai nhận là tự nhận xử lý; nút "Nhận xử lý", "Bỏ nhận", "Chuyển cho" để đổi người phụ trách. Hai người cùng trả lời một khách thì người sau được cảnh báo, không gửi trùng. Thấy "Đang trả lời…" khi đồng nghiệp đang gõ. Cài đặt được: tự trả hội thoại về chung khi khách chờ quá N phút, và chia đều hội thoại mới cho nhân viên đang online.',
      },
      {
        type: 'feature',
        title: 'Khách hàng và tạo đơn nhanh từ hội thoại',
        description:
          'Khung "Thông tin khách" gợi ý khách có sẵn theo SĐT khách đã nhắn, cho liên kết hoặc tạo khách mới (điền sẵn tên, SĐT). Đã liên kết thì xem 5 đơn gần đây và bấm "Tạo đơn nhanh" để mở form đơn đã chọn sẵn khách và kênh bán. Chi tiết khách hàng có thêm tab "Tin nhắn".',
      },
      {
        type: 'feature',
        title: 'Tự động trả lời (mặc định tắt)',
        description:
          'Cài đặt → Trả lời tự động: kịch bản theo từ khoá (không phân biệt dấu), tin ngoài giờ làm việc và lời chào tin đầu tiên. Bot tự dừng khi nhân viên đã trả lời hội thoại. Bật theo từng kênh ở Cài đặt chung → Tin nhắn; nếu Fanpage / OA đang có trả lời tự động riêng thì chỉ nên bật một bên.',
      },
      {
        type: 'feature',
        title: 'Thông báo đơn qua chat (mặc định tắt)',
        description:
          'Khi bật, xác nhận đơn, đã nhận thanh toán và giao thành công được gửi vào hội thoại của khách (khách đã liên kết, còn trong khung trả lời) thay cho ZNS. Gửi chat không được thì tự gửi ZNS như trước.',
      },
      {
        type: 'improvement',
        title: 'Báo cáo marketing: sắp xếp danh sách khách',
        description:
          'Bấm tiêu đề cột để sắp xếp danh sách khách trên toàn bộ dữ liệu (không chỉ trang đang xem); mặc định xếp theo nhóm khách hàng. File Excel xuất cùng thứ tự.',
      },
      {
        type: 'improvement',
        title: 'Số điện thoại khách không bắt buộc',
        description:
          'Tạo khách không cần SĐT (để trống hoặc gõ "0"). SĐT đã có thì vẫn không được trùng, và thông báo lỗi ghi rõ số đó đang thuộc khách nào (tên, mã KH).',
      },
      {
        type: 'improvement',
        title: 'Gộp ô mã số thuế của cửa hàng',
        description:
          'Cài đặt chung → Cửa hàng chỉ còn một ô "Mã số thuế / ĐKKD" (bỏ hai ô trùng "Mã số thuế" và "Số ĐKKD"). Số đã nhập ở ô cũ được chuyển sang ô này nếu ô này đang trống; số này hiện trên trang Chính sách bảo mật công khai.',
      },
      {
        type: 'fix',
        title: '"Người giao" trên đơn hoàn thành',
        description:
          'Trước đây hiện tài khoản đã bấm Hoàn thành. Nay "Người giao" là shipper của chuyến (hoặc shipper ghi trên đơn); nếu người khác xác nhận hộ thì hiện thêm dòng "Xác nhận bởi".',
      },
      {
        type: 'fix',
        title: 'In phiếu đơn gộp',
        description:
          'Phiếu in nhiều đơn gộp nay hiện đúng "Tổng giá trị" trước chiết khấu (trước đây ra 0). Ô mã QR chỉ ghi 2 mã đơn đầu, các đơn còn lại ghi "+N đơn" để không tràn khung.',
      },
    ],
  },
  {
    version: '1.8.1',
    date: '2026-09-30',
    highlight: 'Chia sẻ thưởng đơn hàng cho nhân viên khác, chọn nhiều nhân viên bán cùng trên đơn, sửa ô tìm mã đơn ở chi tiết bonus.',
    items: [
      {
        type: 'feature',
        title: 'Chia sẻ thưởng cho nhân viên khác',
        description:
          'Nhân viên → Bonus → Chi tiết bonus: bấm biểu tượng chia sẻ trên một dòng thưởng, chọn nhân viên nhận thêm. Thưởng chia đều, tổng giữ nguyên, có xem trước số tiền từng người. Nhân viên đã có thưởng trên cùng đơn sẽ bị từ chối. Chỉ admin/quản lý hoặc người được cấp quyền "bonuses:share".',
      },
      {
        type: 'feature',
        title: 'Nhân viên bán cùng trên đơn hàng',
        description:
          'Form đơn hàng có thêm ô "Nhân viên bán cùng". Khi đơn hoàn tất, thưởng bán hàng chia đều cho nhân viên bán + các nhân viên bán cùng; sửa đơn đã hoàn tất sẽ tự tính lại. Nhân viên bán cùng thấy đơn ở danh sách "đơn của tôi".',
      },
      {
        type: 'fix',
        title: 'Ô tìm mã đơn ở Chi tiết bonus',
        description: 'Tìm mã đơn không còn bị cắt chữ, tự bỏ khoảng trắng thừa; Xuất Excel trong drawer ra đúng cột đang bật và đúng mã đơn đang lọc.',
      },
      {
        type: 'improvement',
        title: 'Hộp thoại "Đã có phiên bản mới"',
        description: 'Dùng hộp thoại xác nhận chung của app (Cập nhật / Để sau) thay cho thông báo ở đáy màn hình.',
      },
      {
        type: 'improvement',
        title: 'Hồ sơ nhân viên',
        description: 'Thêm địa chỉ thường trú, mã số BHXH, mã số BHYT; "Địa chỉ" đổi thành "Địa chỉ tạm trú", "Chức danh" thành "Chức vụ". Cập nhật nhân viên ngay trong tab Thông tin nhân viên.',
      },
    ],
  },
  {
    version: '1.8.0',
    date: '2026-09-23',
    highlight:
      'Quản lý phiên đăng nhập (ai đang online, buộc đăng xuất từ xa), audit log toàn hệ thống và % chiết khấu trong báo cáo marketing.',
    items: [
      {
        type: 'feature',
        title: 'Phiên đăng nhập & buộc đăng xuất',
        description:
          'Cài đặt → Phiên đăng nhập: xem ai đang online, thiết bị / trình duyệt / IP và vị trí gần đúng trên bản đồ. Quản trị viên đăng xuất được từng thiết bị hoặc tất cả thiết bị của một người; thiết bị bị thu hồi quay về trang đăng nhập ngay ở thao tác kế tiếp. Có biểu đồ người online 24 giờ, lượt đăng nhập theo ngày và trình duyệt đang dùng; tab "Đáng ngờ" gom phiên lạ (quốc gia mới, thiết bị mới, nhiều lần sai mật khẩu).',
      },
      {
        type: 'feature',
        title: 'Audit log toàn hệ thống',
        description:
          'Cài đặt → Audit log ghi lại đăng nhập, đăng nhập sai, đăng xuất, buộc đăng xuất và mọi thao tác thêm / sửa / xoá. Lọc theo người, hành động, khoảng ngày; mở rộng dòng để xem dữ liệu gửi lên và thay đổi dữ liệu trong cùng thao tác. Chỉ ghi thêm, không sửa/xoá được và tự dọn theo số ngày lưu trong Cài đặt.',
      },
      {
        type: 'feature',
        title: 'Báo cáo marketing có % chiết khấu',
        description:
          'Bảng hiệu quả theo nguồn, danh sách khách và file Excel có thêm cột "% chiết khấu" của đơn đầu = chiết khấu chia cho tiền hàng trước chiết khấu (chưa gồm VAT và phí ship). Thẻ KPI "Chiết khấu đơn đầu" ghi kèm tỉ lệ này.',
      },
      {
        type: 'security',
        title: 'Khoá tài khoản là đăng xuất ngay',
        description:
          'Trước đây khoá tài khoản hoặc đổi mật khẩu mà thiết bị đang đăng nhập vẫn dùng được tới khi hết hạn token. Nay mọi phiên bị huỷ ngay lập tức.',
      },
      {
        type: 'security',
        title: 'Chặn dò mật khẩu và siết phiên đăng nhập',
        description:
          'Giới hạn số lần gọi đăng nhập theo IP và theo email, phiên hết hạn sau 7 ngày, tự đăng xuất khi không hoạt động quá thời gian cài đặt, mật khẩu mới tối thiểu 10 ký tự. Đăng xuất nay huỷ token ở máy chủ chứ không chỉ xoá trên máy người dùng.',
      },
      {
        type: 'improvement',
        title: 'Màn đăng nhập báo lỗi rõ ràng hơn',
        description:
          'Bỏ hộp thông báo nổi che form; lỗi hiện ngay trong khung đăng nhập và nói đúng nguyên nhân (sai mật khẩu, không kết nối được máy chủ, phiên bị thu hồi).',
      },
      {
        type: 'feature',
        title: 'Đồng bộ BHXH cho kỳ lương đã tạo',
        description:
          'Sửa "Mức lương đóng BHXH" trên hồ sơ nhân viên sau khi đã tạo kỳ lương thì bấm "Đồng bộ BHXH" trên màn Bảng lương để cập nhật các phiếu của kỳ và tính lại BHXH / BHYT / BHTN, không phải sửa tay từng phiếu. Kỳ đã chốt được giữ nguyên; mỗi lần đồng bộ ghi lại vào Audit log (ai làm, kỳ nào, phiếu nào đổi từ số nào sang số nào).',
      },
      {
        type: 'improvement',
        title: 'Danh sách nhân viên có cột Lương đóng BHXH',
        description:
          'Thêm cột "Lương đóng BHXH" ngay sau cột Lương CB (bật/tắt bằng nút lưới) và cột này cũng có trong file Excel. File Excel nay chỉ xuất cột lương cho người được xem lương.',
      },
      {
        type: 'security',
        title: 'Chốt kỳ lương phải có quyền',
        description:
          'Trước đây mọi tài khoản đã đăng nhập đều gọi được chức năng chốt (khoá) kỳ lương. Nay chỉ người có quyền sửa bảng lương mới chốt được.',
      },
      {
        type: 'improvement',
        title: 'Phiếu bán hàng có mã QR cho shipper',
        description:
          'Góc phải đầu phiếu in có mã QR kèm mã đơn: shipper quét là mở thẳng trang xử lý nhanh để cập nhật giao hàng, không phải tìm đơn thủ công. Mã thanh toán vẫn ở cuối phiếu và được ghi nhãn "THANH TOÁN" nên không nhầm hai mã. Logo và thông tin cửa hàng ở đầu phiếu cũng được canh lại: logo in khổ lớn hơn, dễ nhìn hơn.',
      },
      {
        type: 'improvement',
        title: 'Chi tiết đơn hàng có ảnh sản phẩm',
        description:
          'Bảng hàng trong chi tiết đơn (và đơn trả hàng) thêm cột ảnh để nhận ra mặt hàng ngay, bấm vào xem ảnh lớn. Ảnh lấy theo mã sản phẩm lúc xem nên luôn khớp ảnh mới nhất trong danh mục.',
      },
      {
        type: 'fix',
        title: 'Huỷ đơn thì mã QR thanh toán hết hiệu lực',
        description:
          'Mã QR OnePay đã in/gửi cho khách sống khoảng 1 ngày. Trước đây huỷ đơn không huỷ mã nên khách vẫn quét trả tiền cho đơn không còn hiệu lực; nay huỷ đơn là mã chết theo. (Sửa số tiền đơn thì mã cũ vốn đã tự huỷ, lần in sau thu đúng số mới.)',
      },
      {
        type: 'improvement',
        title: 'Tìm kiếm trong Hướng dẫn sử dụng',
        description:
          'Trang Hướng dẫn có ô tìm kiếm (gõ không dấu vẫn ra) và bộ lọc theo phân hệ; từ khoá khớp được tô sáng để thấy ngay chỗ cần đọc.',
      },
      {
        type: 'improvement',
        title: 'Cài đặt sắp xếp lại theo nhóm',
        description:
          'Các thẻ Cài đặt gom lại theo thứ tự thường dùng (Cửa hàng → Ứng dụng → Nhân viên → Tích điểm → Marketing → Đăng nhập & bảo mật → …), thêm nhóm "Phiên đăng nhập & audit log".',
      },
    ],
  },
  {
    version: '1.7.0',
    date: '2026-09-22',
    highlight: 'Bảng lương tính bảo hiểm bắt buộc BHXH / BHYT / BHTN, tra cứu và xuất Excel có dòng tổng.',
    items: [
      {
        type: 'feature',
        title: 'Bảo hiểm bắt buộc trên bảng lương',
        description: 'Khai báo "Mức lương đóng BHXH" trên hồ sơ nhân viên; mỗi kỳ lương tự trừ phần nhân viên đóng (BHXH 8%, BHYT 1,5%, BHTN 1%) vào thực lĩnh. Phiếu lương (màn hình và bản in) có nhóm "Bảo hiểm bắt buộc" riêng ghi rõ mức lương đóng và từng khoản.',
      },
      {
        type: 'feature',
        title: 'Chi phí bảo hiểm công ty đóng — chỉ kế toán / giám đốc xem',
        description: 'Phần công ty đóng (BHXH 17,5%, BHYT 3%, BHTN 1%) hiện trong phiếu lương và bảng lương cho kế toán, giám đốc, quản trị viên; nhân viên không thấy và không in trên phiếu lương.',
      },
      {
        type: 'feature',
        title: 'Cấu hình tỉ lệ và mức trần bảo hiểm',
        description: 'Cài đặt chung → tab "Bảo hiểm bắt buộc": sửa tỉ lệ và mức trần (lương cơ sở, lương tối thiểu vùng). Mỗi kỳ lương lưu lại tỉ lệ lúc tạo nên đổi cài đặt không làm lệch các kỳ đã tính.',
      },
      {
        type: 'improvement',
        title: 'Bảng lương: cột bảo hiểm và Excel có dòng Tổng',
        description: 'Thêm cột Mức lương đóng BHXH, NV đóng BHXH / BHYT / BHTN, Tổng NV đóng BH (và cột công ty đóng cho kế toán / giám đốc). Xuất Excel đúng các cột đang hiện, kèm dòng "Tổng" để đối chiếu tiền nộp BHXH và tổng chi lương.',
      },
      {
        type: 'improvement',
        title: 'Không cho xoá nhân viên còn dữ liệu liên quan',
        description: 'Nút "Xoá" bị khoá và ghi rõ lý do khi nhân viên còn tài khoản đăng nhập, đơn hàng, chuyến giao, phiếu thu/chi, thưởng, bảng lương, chấm công hoặc đang quản lý nhân viên khác.',
      },
      {
        type: 'security',
        title: 'Chặn xoá dữ liệu đã phát sinh giao dịch',
        description: 'Không xoá được: sản phẩm đã có trong đơn / phiếu kho; đơn "Mở" đã có thanh toán, thưởng hoặc đang trong chuyến giao; phiếu lương của kỳ đã chốt; tài khoản ngân hàng, nhóm khách, nhóm sản phẩm, loại NCC, kênh bán, vị trí kho, cửa hàng đang được sử dụng. Trước đây xoá tài khoản ngân hàng / nhóm khách / loại NCC / cửa hàng có thể xoá kéo theo nhà cung cấp, khách hàng, đơn hàng — nay đã chặn. Thông báo nêu rõ lý do; dữ liệu không còn dùng thì tắt "Đang hoạt động" / "Ngừng kinh doanh" thay vì xoá.',
      },
      {
        type: 'fix',
        title: 'Tạo / chốt kỳ lương cập nhật bảng ngay',
        description: 'Sau khi tạo kỳ, bảng tự chuyển sang đúng tháng vừa tạo; sau khi chốt, trạng thái đã duyệt hiện ngay — không cần tải lại trang.',
      },
      {
        type: 'fix',
        title: 'Cột mới trong bảng không còn bị ẩn',
        description: 'Người đã từng chỉnh cột hiển thị giờ vẫn thấy các cột mới thêm (theo mặc định), thay vì bị ẩn mất.',
      },
      {
        type: 'fix',
        title: 'Sửa giao diện ô nhập',
        description: 'Ô "Địa chỉ giao hàng" khi tạo đơn không còn bị chồng khung; placeholder ô nhập số nhạt màu để không nhầm với giá trị đã nhập; khu Cài đặt chung thoáng hơn, ô số tiền có phân cách hàng nghìn.',
      },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-09-21',
    highlight: 'Báo cáo marketing tính cả khách cũ quay lại cho nguồn đã mang khách về, thêm bảng KPI nguồn theo tháng.',
    items: [
      {
        type: 'feature',
        title: 'Khách cũ tính cho nguồn thu hút',
        description: 'Khách mua lần đầu qua một kênh marketing (vd. Google), các tháng sau quay lại mua qua bất kỳ kênh nào thì doanh thu vẫn tính cho Google. Bảng "Hiệu quả theo nguồn" có thêm khách cũ quay lại, DT khách cũ và Tổng DT nguồn; thêm thẻ KPI "Khách cũ quay lại" và "Tổng DT nguồn MKT".',
      },
      {
        type: 'feature',
        title: 'Bảng KPI nguồn theo tháng',
        description: 'Mỗi nguồn một dòng, mỗi tháng một cột: Tổng DT nguồn, DT khách mới, DT khách cũ, số khách mới, số khách cũ quay lại. Chọn kỳ "Năm nay" để theo dõi KPI hằng tháng của MKT.',
      },
      {
        type: 'improvement',
        title: 'Danh sách khách có tab Khách mới / Khách cũ quay lại / Tất cả',
        description: 'Xem số đơn và doanh thu trong kỳ của từng khách, xuất Excel theo tab đang chọn.',
      },
      {
        type: 'improvement',
        title: 'Tỉ lệ mua lại (cohort) luôn hiện 12 tháng gần nhất',
        description: 'Chọn "Tháng này" vẫn thấy khách thu hút các tháng trước đã quay lại bao nhiêu % sau 30/60/90 ngày, thay vì chỉ có tháng hiện tại (thường chưa đủ ngày theo dõi nên toàn "—").',
      },
      {
        type: 'fix',
        title: 'App trên điện thoại tự cập nhật bản mới',
        description: 'App cài trên màn hình chính tự kiểm tra bản mới mỗi khi mở lại và định kỳ. Khi có bản mới sẽ hiện thông báo "Đã có phiên bản mới" — bấm Cập nhật (hoặc app tự cập nhật khi bạn chuyển sang app khác), không tải lại ngang khi đang nhập liệu.',
      },
      {
        type: 'fix',
        title: 'Không còn bị tai thỏ / thanh trạng thái iPhone che',
        description: 'Thanh trên cùng và menu chừa đúng vùng tai thỏ và thanh home trên iPhone X trở lên; chân trang trên điện thoại nằm cuối trang thay vì luôn ghim ở đáy che nội dung.',
      },
    ],
  },
  {
    version: '1.5.1',
    date: '2026-09-21',
    highlight: 'Báo cáo marketing thêm số cành, tiền chậu/trang trí, chiết khấu của đơn đầu; doanh thu không còn gồm phí ship.',
    items: [
      {
        type: 'feature',
        title: 'Số cành, chậu/trang trí, chiết khấu của đơn đầu',
        description: 'Báo cáo marketing có thêm 3 thẻ KPI, cột trong bảng "Hiệu quả theo kênh", "Danh sách khách mới" và file Excel. Số cành tính theo đơn vị hàng ("cành" = 1, "1c"/"2c" = 1/2 cành mỗi đơn vị, nhân số lượng); chậu/trang trí là hàng đơn vị "chiếc"; chiết khấu là số tiền giảm của đơn. Cả 3 chỉ tính trên đơn đầu tiên và đã trừ hàng trả.',
      },
      {
        type: 'improvement',
        title: 'Doanh thu marketing không gồm phí ship',
        description: 'Mọi số doanh thu trong báo cáo marketing đã trừ VAT, phí ship và hàng trả, nên thấp hơn báo cáo bán hàng đúng bằng phần phí ship. Khung "Cách tính" và ⓘ trên từng chỉ số giải thích rõ.',
      },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-09-16',
    highlight:
      'Ra mắt Chuyến giao hàng: gom đơn theo tuyến Google, km và bonus chốt ngay khi lên tuyến; báo cáo marketing tập trung vào kênh quảng cáo.',
    items: [
      {
        type: 'feature',
        title: 'Chuyến giao hàng',
        description: 'Gom nhiều đơn cùng tuyến thành một chuyến, Google đề xuất thứ tự giao tối ưu. Xem km cả tuyến, km tiết kiệm và bonus trước khi tạo; in tem QR, bắt đầu giao, hoàn thành từng đơn kèm ảnh và người nhận. Xem cách dùng trong Hướng dẫn sử dụng.',
      },
      {
        type: 'feature',
        title: 'Km chuyến theo Google Maps',
        description: 'Km tính theo tuyến ô tô có tính kẹt xe và được chốt tại thời điểm tạo chuyến. Nút "Mở Google Maps" mở đúng lộ trình đã tính; mục "Các con số này Google tính thế nào?" giải thích từng chỉ số.',
      },
      {
        type: 'improvement',
        title: 'Bonus chốt ngay khi lên tuyến',
        description: 'Shipper bấm kết thúc chuyến là bonus được ghi theo km đã chốt, không phải nhập lại km. Chỉ quản lý được điều chỉnh km (kèm lý do), hoặc nhập km khi Google không tính được tuyến.',
      },
      {
        type: 'improvement',
        title: 'Thưởng giao hàng chỉ tính qua chuyến',
        description: 'Không còn thưởng giao hàng lẻ theo từng đơn: mọi thưởng giao hàng đi qua Chuyến giao hàng (tạo được chuyến 1 đơn), tính theo km cả tuyến, ghi nhận khi kết thúc chuyến và tính vào ngày kết thúc chuyến (không theo ngày lập đơn).',
      },
      {
        type: 'improvement',
        title: 'Nhắc kết thúc chuyến để không quên ghi nhận bonus',
        description: 'Bonus chỉ được ghi khi bấm "Kết thúc chuyến". Giao xong đơn cuối (ở chuyến hoặc màn quét mã) hệ thống hỏi kết thúc chuyến luôn; chuyến đã giao hết nhưng chưa kết thúc được nhắc trên màn chi tiết và gắn nhãn "Chờ kết thúc chuyến" trong danh sách.',
      },
      {
        type: 'improvement',
        title: 'Bắt buộc ảnh giao hàng cho đơn trong chuyến',
        description: 'Đơn thuộc chuyến chỉ hoàn thành được khi có ảnh giao hàng, kể cả khi quản lý xác nhận; chuyến chưa kết thúc được nếu còn đơn hoàn thành thiếu ảnh.',
      },
      {
        type: 'fix',
        title: 'Chi tiết bonus nhân viên',
        description: '"Tổng km giao hàng" hiển thị đúng (trước đây luôn ra 0 km); thêm cột "Chuyến" — bấm mã chuyến để mở chi tiết chuyến đối chiếu km — và file Excel lấy km theo từng dòng thưởng.',
      },
      {
        type: 'fix',
        title: 'Nhập sai số điện thoại khi giao hàng xong',
        description: 'Form "Giao hàng xong", nhập/điều chỉnh km chuyến và chuyển khách hàng không còn báo lỗi hệ thống khi có ô nhập sai; chỉ hiện lỗi dưới ô và tự cuộn tới ô đó.',
      },
      {
        type: 'improvement',
        title: 'Breadcrumb hiển thị mã chuyến',
        description: 'Trang chi tiết chuyến hiển thị mã CGxxxxx thay cho "Chi tiết #id", giống các trang đơn hàng, sổ cái, khách hàng.',
      },
      {
        type: 'improvement',
        title: 'Tạo chuyến ngay từ màn Đơn hàng',
        description: 'Dải "Giao hôm nay" và "Giao ngày mai" có nút "Tạo chuyến" mở màn tạo chuyến đã lọc sẵn đơn của ngày đó (hiện với người có quyền tạo chuyến).',
      },
      {
        type: 'feature',
        title: 'Chọn nhân viên giao trong tuyến đề xuất',
        description: 'Quản lý chọn shipper ngay khi tạo chuyến (mặc định là shipper đang phụ trách các đơn). Tài khoản shipper chỉ thấy đơn mình phụ trách và chuyến tự gán cho chính mình.',
      },
      {
        type: 'feature',
        title: 'Gợi ý địa chỉ khi nhập đơn',
        description: 'Chọn địa chỉ từ gợi ý của Google để lưu đúng vị trí giao hàng (cần cấu hình khoá Google cho trình duyệt).',
      },
      {
        type: 'improvement',
        title: 'Chuyến tự cập nhật khi sửa địa chỉ đơn',
        description: 'Chuyến chưa bắt đầu giao tự tính lại km và bonus; chuyến đang giao giữ số đã chốt và hiện cảnh báo để quản lý kiểm tra.',
      },
      {
        type: 'fix',
        title: 'Địa chỉ bị Google hiểu nhầm sang tỉnh khác',
        description: 'Sau sáp nhập, địa chỉ kiểu cũ như "Phường 10, Quận 6" có thể bị trỏ sang Vũng Tàu làm km sai. Nay hệ thống ưu tiên vị trí quanh cửa hàng, loại kết quả quá xa và cảnh báo chặng dài bất thường.',
      },
      {
        type: 'fix',
        title: 'Km riêng từng đơn đo theo ô tô',
        description: 'Trước đây km riêng của đơn đo theo đường đi bộ nên ô "Tiết kiệm" không chính xác. Nay đo cùng chuẩn với km cả tuyến; chỉ áp dụng cho đơn mới, bonus đã chốt không thay đổi.',
      },
      {
        type: 'improvement',
        title: 'Báo cáo marketing chỉ tính kênh quảng cáo',
        description: 'Chỉ tính khách có đơn đầu tiên từ Google, Facebook, Tiktok, Zalo; danh sách kênh cấu hình được trong Cài đặt → Báo cáo marketing.',
      },
      {
        type: 'security',
        title: 'Ẩn số điện thoại khách trong báo cáo marketing',
        description: 'Không hiển thị SĐT trong bảng và file Excel xuất ra; vẫn tìm kiếm được khách theo SĐT.',
      },
      {
        type: 'improvement',
        title: 'Định dạng ngày thống nhất dd-mm-yyyy',
        description: 'Toàn bộ danh sách, báo cáo, phiếu in và ô chọn ngày hiển thị cùng một định dạng.',
      },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-08-26',
    highlight: 'Ra mắt Báo cáo tồn kho, làm mới giao diện toàn ứng dụng, tăng bảo mật và nhiều tiện ích điều hướng.',
    items: [
      {
        type: 'feature',
        title: 'Báo cáo tồn kho (Nhập – Xuất – Tồn)',
        description: 'Tồn đầu kỳ, nhập, xuất, tồn cuối kỳ (SL + giá trị) theo từng mã hàng; lọc theo ngày/tên-mã/NCC/nhóm hàng; biểu đồ Top 10 và xuất Excel.',
      },
      {
        type: 'improvement',
        title: 'Điều chỉnh giao diện toàn bộ ứng dụng',
        description: 'Danh sách, báo cáo, biểu mẫu và trang chi tiết được làm mới đồng bộ theo phong cách hiện đại, thống nhất.',
      },
      {
        type: 'feature',
        title: 'Hướng dẫn sử dụng & Nhật ký cập nhật',
        description: 'Mục "Trợ giúp" mới trên menu; popup "Có gì mới" khi có bản phát hành, phiên bản hiển thị cuối sidebar, và quản trị viên gửi được email thông báo tính năng mới cho người dùng.',
      },
      {
        type: 'feature',
        title: 'Cài đặt như ứng dụng riêng (PWA)',
        description: 'Cài "Hoa Chân Thật" lên Dock/Taskbar/màn hình chính trên macOS, Windows, iPhone và Android — mở nhanh, chạy cửa sổ riêng, tự cập nhật phiên bản mới. Xem cách cài trong Hướng dẫn sử dụng.',
      },
      {
        type: 'feature',
        title: 'Thanh toán OnePay bằng mã QR trong đơn hàng',
        description: 'Đơn còn nợ có nút "Thanh toán OnePay" mở mã QR cho khách quét, tự nhận kết quả và ghi sổ. Trong cửa sổ này có nút "Tạo lại mã QR" cho trường hợp mã hết hạn (khoảng 1 ngày) để gửi lại mã mới cho khách.',
      },
      {
        type: 'feature',
        title: 'Trang chi tiết nhân viên',
        description: 'Bấm mã NV để mở trang riêng với các tab Thông tin, Lịch sử đơn hàng, Lịch sử thay đổi; nút Cập nhật/Xoá trên header theo quyền.',
      },
      {
        type: 'improvement',
        title: 'Điều hướng nhất quán toàn ứng dụng',
        description: 'Các link (mã đơn, khách hàng, NCC, sản phẩm, phiếu kho…) có icon nhận dạng; mọi trang chi tiết có nút "Quay lại" thống nhất.',
      },
      {
        type: 'improvement',
        title: 'Làm mới giao diện mục Cài đặt',
        description: 'Cài đặt chung, Ngân hàng, Người dùng, Vai trò, Phân quyền được thiết kế lại đồng bộ; nút thao tác hiển thị theo quyền.',
      },
      {
        type: 'security',
        title: 'Mã hoá khoá tích hợp (OnePay, Zalo, Email…)',
        description: 'Khoá bí mật được mã hoá chuẩn AES-256-GCM, không còn hiển thị trên giao diện; để trống khi lưu nghĩa là giữ nguyên giá trị cũ.',
      },
      {
        type: 'fix',
        title: 'Sửa tìm kiếm ở danh sách Người dùng và Ngân hàng',
        description: 'Ô tìm kiếm trước đây không lọc đúng dữ liệu.',
      },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-08-25',
    highlight: 'Làm mới khu vực Báo cáo và các màn hình Lương, Bonus, Khách hàng, Nhà cung cấp.',
    items: [
      {
        type: 'improvement',
        title: 'Làm mới giao diện Báo cáo',
        description: 'Thẻ KPI, biểu đồ Top 10, chế độ xem Bảng/Biểu đồ thống nhất trên các báo cáo.',
      },
      {
        type: 'improvement',
        title: 'Làm mới giao diện Lương, Bonus, Khách hàng, Nhà cung cấp',
      },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-08-24',
    highlight: 'Giao diện mới cho toàn ứng dụng: đơn hàng, kho, sổ cái, sản phẩm.',
    items: [
      {
        type: 'improvement',
        title: 'Giao diện mới toàn ứng dụng',
        description: 'Thanh điều hướng, màn hình Đơn hàng, Tạo đơn, Quản lý kho, Sản phẩm và Sổ cái được thiết kế lại.',
      },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-08-17',
    highlight: 'Đăng nhập bằng Google và hoàn tác phiếu sổ cái.',
    items: [
      { type: 'feature', title: 'Đăng nhập bằng tài khoản Google (SSO)' },
      { type: 'feature', title: 'Hoàn tác (revert) phiếu sổ cái' },
    ],
  },
];

export const LATEST_VERSION = RELEASES[0]?.version ?? '0.0.0';

const LAST_SEEN_KEY = 'release_log_last_seen_version';

/** So sánh semver đơn giản: 1 nếu a > b, -1 nếu a < b, 0 nếu bằng. */
export const compareVersions = (a: string, b: string): number => {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);

  for (let i = 0; i < len; i += 1) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff > 0 ? 1 : -1;
  }
  return 0;
};

/** Các bản phát hành mới hơn bản user đã xem (null = chưa xem bản nào). */
export const getUnseenReleases = (lastSeenVersion: string | null): Release[] => {
  if (!lastSeenVersion) return RELEASES;
  return RELEASES.filter((release) => compareVersions(release.version, lastSeenVersion) > 0);
};

export const readLastSeenVersion = (): string | null => localStorage.getItem(LAST_SEEN_KEY);

export const markReleasesSeen = (): void => localStorage.setItem(LAST_SEEN_KEY, LATEST_VERSION);
